import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Main from './Components/Main';
import Health from './Components/Health';
import Offline from './Components/Offline';
import Landing from './Components/Landing';
import Account from './Components/Account';
import Marketplace from './Components/Marketplace';
import ProductDetail from './Components/ProductDetail';
import InventoryItemDetail from './Components/InventoryItemDetail';
import RepairCatalogDetail from './Components/RepairCatalogDetail';
import Cart from './Components/Cart';
import MyRFQs from './Components/MyRFQs';
import Checkout from './Components/Checkout';
import MyOrders from './Components/MyOrders';
import MyInvoices from './Components/MyInvoices';
import MyRmas from './Components/MyRmas';
import MyShipments from './Components/MyShipments';
import PrivacyPolicy from './Components/PrivacyPolicy';
import CookieConsent from './Components/CookieConsent';
import NavigationProgressBar from './Components/NavigationProgressBar';
import { getPortalStatus, getUserDetails, clearSessionToken, getSessionToken } from './Services/services';
import { BrandingProvider } from './Context/BrandingContext';
import { LoginModalProvider } from './Context/LoginModalContext';
import { ListScrollCacheProvider } from './Context/ListScrollCacheContext';
import LoginModal from './Components/LoginModal';

/**
 * Checks the live Salesforce engagement status on every route change.
 * - Rejected users have their session destroyed and are redirected to home.
 * - Approved users get their Redux role updated immediately without re-login.
 */
function RoleRefresher() {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!getSessionToken()) return;

    // Skip pure auth/utility routes
    const skip = ['/', '/login', '/health', '/offline', '/privacy'];
    if (skip.includes(location.pathname)) return;

    getUserDetails()
      .then((res) => {
        const data = res?.data;
        if (!data) return;

        if (data.is_rejected) {
          // Session deleted server-side; clear client-side and send to home.
          clearSessionToken();
          dispatch({ type: 'CLEAR_AUTH' });
          window.location.replace('/');
          return;
        }

        // Update Redux with fresh role + profile so the UI reflects changes
        // (e.g. Approved → standard features visible) without re-login.
        if (data.role) {
          dispatch({
            type: 'SET_AUTH',
            payload: {
              email: data.email,
              role: data.role,
              isSupportRep: data.isSupportRep,
              fullName: data.full_name,
              company: data.company,
              phone: data.phone,
              jobTitle: data.job_title,
              partSales: data.part_sales,
              mro: data.mro,
              connectRoles: data.roles,
            },
          });
        }
      })
      .catch(() => {
        // If 401 the session has expired — let individual pages handle redirect.
      });
  // Re-run on every pathname change (covers both SPA nav and reload via mount).
  }, [location.pathname, dispatch]);

  return null;
}

function CatalogLegacyRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/catalog${search}`} replace />;
}

// Check if hostname is a bare/root domain (no tenant subdomain)
function isBareDomain() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Azure static domains - show landing page
  if (hostname.includes('azurestaticapps.net')) {
    return true;
  }
  
  // Define the valid domain suffixes (environment roots)
  const domainSuffixes = [
    'dev.avsight-ai.com',
    'stgn.avsight-ai.com', 
    'avsight-ai.com'
  ];
  
  // Check if this is a bare domain (root without tenant subdomain)
  for (const suffix of domainSuffixes) {
    if (hostname === suffix) {
      return true;
    }
  }
  
  return false;
}

// Check if hostname has a valid tenant subdomain
function hasValidTenantSubdomain() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Azure static domains - never valid for login
  if (hostname.includes('azurestaticapps.net')) {
    return false;
  }
  
  // Define the valid domain suffixes (environment roots)
  const domainSuffixes = [
    'dev.avsight-ai.com',
    'stgn.avsight-ai.com', 
    'avsight-ai.com'
  ];
  
  for (const suffix of domainSuffixes) {
    if (hostname === suffix) {
      // Bare domain (no tenant subdomain) - not valid for login
      return false;
    }
    if (hostname.endsWith('.' + suffix)) {
      // Has a subdomain prefix - valid tenant domain
      return true;
    }
  }
  
  // localhost or other domains - allow for local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  
  return false;
}

function App() {
  const isValidTenant = hasValidTenantSubdomain();
  const isBare = isBareDomain();
  const [portalStatus, setPortalStatus] = useState({ checked: false, online: true });
  const currentPath = window.location.pathname;
  
  // Check portal status on mount (for tenant domains, not on /health, /offline, or /login)
  useEffect(() => {
    if (!isValidTenant || isBare || currentPath === '/health' || currentPath === '/offline' || currentPath === '/login') {
      setPortalStatus({ checked: true, online: true });
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await getPortalStatus();
        const isOnline = response.data?.online === true;
        setPortalStatus({ checked: true, online: isOnline });
        
        // If portal is offline and we're not on /offline, redirect
        if (!isOnline && currentPath !== '/offline') {
          window.location.replace('/offline');
        }
      } catch (error) {
        console.log('Portal status check failed:', error);
        // On error, assume online to not block users
        setPortalStatus({ checked: true, online: true });
      }
    };

    checkStatus();
  }, [isValidTenant, isBare, currentPath]);

  // Show loading while checking portal status (only for main route on tenant domains)
  if (!portalStatus.checked && currentPath === '/' && isValidTenant) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <BrandingProvider>
      <LoginModalProvider>
        <ListScrollCacheProvider>
        <Router>
          <NavigationProgressBar />
          <RoleRefresher />
          <LoginModal />
          <CookieConsent />
          <Routes>
          {/* Health check page - always accessible */}
          <Route path="/health" element={<Health />} />
          
          {/* Offline page - accessible when portal is disabled */}
          <Route path="/offline" element={<Offline />} />

          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* Landing page for bare/root domains */}
          {isBare && (
            <>
              <Route path="/" element={<Landing />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
          
          {/* Tenant domain routes - Landing page first, login flow on /login */}
          {isValidTenant && portalStatus.online && (
            <>
              <Route path="/" element={<Landing isTenant={true} />} />
              <Route path="/login" element={<Main />} />
              <Route path="/account" element={<Account />} />
              <Route path="/catalog" element={<Marketplace />} />
              <Route path="/catalog/repair/:itemId" element={<RepairCatalogDetail />} />
              <Route path="/marketplace" element={<CatalogLegacyRedirect />} />
              <Route path="/inventory/item/:itemId" element={<InventoryItemDetail />} />
              <Route path="/product/:productId" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/my-rfqs" element={<MyRFQs />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/invoices" element={<MyInvoices />} />
              <Route path="/rmas" element={<MyRmas />} />
              <Route path="/shipments" element={<MyShipments />} />
            </>
          )}
          </Routes>
        </Router>
        </ListScrollCacheProvider>
      </LoginModalProvider>
    </BrandingProvider>
  );
}

export default App;

